import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { BaseController } from '~/utils/base-controller';
import APIV5 from '~/models/api';
import GaugeController from './gauge-chart.charjs.plugin';
import './gauge-chart.scss';

/**
 * @implements {angular.IController}
 */
export default class GaugeChartController extends BaseController {
	/** @type {angular.IRootElementService} */
	$element;

	total = 0;

	chart;

	/** @type {Dashboard.GaugeChartWidgetOptions['options']} */
	options;

	hasOptions = true;

	/* @ngInject */
	// eslint-disable-next-line no-useless-constructor
	constructor($injector, $element) {
		super($injector);

		this.$element = $element;
	}

	/**
	 *
	 * @returns {Promise<import("chart.js/auto").ChartConfiguration<'doughnut'>>}
	 */
	async createOptions() {
		if (!this.options?.data?.thresholds) {
			console.warn('Options are required');
			this.hasOptions = false;
		}

		/** @type {Dashboard.GaugeChartWidgetOptionsOptions['thresholds']} */
		const tresholds = this.options?.data?.thresholds ?? [];

		const colorsKids = tresholds.map(threshold => threshold.color[0].kid);
		const values = tresholds.map(threshold => threshold.value);
		const labels = tresholds.map(threshold => threshold.label);

		const representations = await APIV5.instance.repreListAll({
			items: colorsKids,
			mode: 'all',
		});

		const colorCodes = {};
		for (const representation of Object.values(representations.result)) {
			colorCodes[representation.kid] = representation.repre_all?.['K_NODE;HEXADECIMAL']?.name ?? '000';
		}

		const colors = colorsKids.map(kid => `#${colorCodes[kid]}`);

		// TODO: create gauge

		/** @type {import("chart.js/auto").ChartConfiguration<'doughnut'>} */
		const options = {
			plugins: [GaugeController, ChartDataLabels],
			type: 'gauge',
			data: {
				labels,
				datasets: [{
					value: this.total,
					minValue: this.options?.data?.minValue ?? 0,
					data: values,
					backgroundColor: colors,
					borderWidth: 1,
				}],
			},
			options: {
				needle: {
					radiusPercentage: 1,
					widthPercentage: 1.2,
					lengthPercentage: 80,
					color: 'rgba(0, 0, 0, 1)',
				},
				valueLabel: {
					display: true,
					formatter: value => `$${ Math.round(value)}`,
					color: 'rgba(255, 255, 255, 1)',
					backgroundColor: 'rgba(0, 0, 0, 1)',
					borderRadius: 5,
					padding: {
						top: 10,
						bottom: 10,
					},
				},
				plugins: {
					datalabels: {
						display: true,
						formatter(value, context) {
							return context.chart.data.labels[context.dataIndex];
						},
						color: 'rgba(255, 255, 255, 1.0)',
						borderWidth: 1,
						borderRadius: 5,
						font: {
							weight: 'bold',
						},
					},
				},
				maintainAspectRatio: false,
			},
		};

		return options;
	}

	async $onChanges(changes) {
		if (changes && this.chart) {
			this.hasOptions = true;
			const newOptions = await this.createOptions();
			this.chart.data = newOptions.data;
			this.chart.update();
		}
	}

	async $onInit() {
		/** @type {HTMLCanvasElement} */
		// @ts-ignore
		const context = this.$element.find('canvas.gauge-chart').get(0);

		const options = await this.createOptions();

		this.chart = new Chart(context, options);
	}
}
